import React from "react";
import debug from "debug";
import { Helmet } from "react-helmet";
import { History } from "history";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import { RouteComponentProps, withRouter } from "react-router";
import { Fab } from "@material-ui/core";
import { NodeType } from "@graphql";
import { ThemeType } from "@theme";
import { store } from "@store";
import { dateFormat } from "@bonliva-crm/locale";
import { GeneralSearchStore, GeneralSearchData } from "./GeneralSearchStore";
import Form, { FormProps, Children } from "./Form/Form";
import SearchPageLayout, { SearchPageLayoutProps } from "./components/SearchPageLayout";
import SearchResultLayout from "./components/SearchResultLayout";
import SearchResult from "./SearchResult";

const log = debug("app:Search");

type RenderCallback = (
  search: GeneralSearchData,
  store: GeneralSearchStore,
  searchState: State
) => React.ReactNode;

type OwnProps<T> = {
  beforeContent?: React.ReactNode;
  nodeType: NodeType;
  title: string;
  onNew?: () => any;
  renderHeader?: RenderCallback;
  fetchData: (skip: number, store: GeneralSearchStore) => Promise<T[]>;
  fetchCount?: (store: GeneralSearchStore) => Promise<number>;
  renderActionBar?: RenderCallback;
  renderDetail: (props: {
    item: T;
    history: History;
    dateFormat: string;
    groupBy: string;
    search: GeneralSearchData;
  }) => JSX.Element;
  group?: GroupBy<T>;
  children: Children;
  noResultsMessage?: string;
  hideClearFilter?: boolean;
  FormProps?: Partial<FormProps>;
  offsetPulldownMenuTop?: number;
  SearchPageLayoutProps?: Partial<SearchPageLayoutProps>;
  hideSearchBar?: boolean;
};

type GroupBy<T> = {
  render: (item: T, groupBy: string) => JSX.Element;
  by: (groupBy: string) => (item: T) => string;
  options: LabelValue[];
};

type Props<T> = OwnProps<T> &
  RouteComponentProps<{}> & {
    theme?: ThemeType;
  };

type State = {
  count?: number;
};

@observer
class Search<T> extends React.Component<Props<T>, State> {
  state: State = {};
  searchResult: SearchResult;

  componentDidMount() {
    store().generalSearchStore.registerSearchCallback(this.searchResult.searchCallback);
  }

  getCount = async () => {
    if (!this.props.fetchCount) {
      return;
    }
    let count: number = null;
    this.setState((s) => ({ ...s, count }));
    count = await this.props.fetchCount(store().generalSearchStore);

    this.setState((s) => ({ ...s, count }));
  };

  renderGroup = (x: T) => {
    return this.props.group.render(x, store().generalSearchStore.groupBy);
  };

  renderHeader = () => {
    const searchStore = store().generalSearchStore;

    const header =
      typeof this.props.renderHeader === "function"
        ? this.props.renderHeader(toJS(searchStore.searchParams), searchStore, this.state)
        : undefined;

    return header;
  };

  render() {
    log("render", this.props);

    const { beforeContent } = this.props;
    const searchStore = store().generalSearchStore;

    const actions = this.props.renderActionBar(
      toJS(searchStore.searchParams),
      searchStore,
      this.state
    );

    return (
      <React.Fragment>
        <Helmet>
          <title>CRM | {this.props.title}</title>
        </Helmet>

        <SearchPageLayout
          actionBar={actions}
          beforeContent={beforeContent}
          onClear={!this.props.hideClearFilter ? searchStore.clearForm : null}
          header={this.renderHeader()}
          filter={
            <Form
              nodeType={this.props.nodeType}
              store={searchStore}
              hideSearchBar={this.props.hideSearchBar}
            >
              {this.props.children}
            </Form>
          }
          stickyContainerProps={{
            hidePullBtn: true,
            offsetTop: this.props.offsetPulldownMenuTop
          }}
          content={
            <SearchResultLayout
              groupBy={searchStore.groupBy}
              groupByOptions={this.props.group && this.props.group.options}
              onGroupBy={searchStore.setGroupBy}
              content={
                <SearchResult
                  ref={(r) => (this.searchResult = r)}
                  scrollContainerId="search_page_scrollable"
                  groupBy={this.props.group && this.props.group.by(searchStore.groupBy)}
                  renderGroup={this.props.group && this.renderGroup}
                  renderDetail={(item) =>
                    this.props.renderDetail({
                      item,
                      dateFormat,
                      history: this.props.history,
                      groupBy: searchStore.groupBy,
                      search: searchStore.searchParams
                    })
                  }
                  fetchData={(skip) => {
                    if (skip === 0) {
                      this.getCount();
                    }
                    return this.props.fetchData(skip, store().generalSearchStore);
                  }}
                  noResultsMessage={this.props.noResultsMessage}
                />
              }
            />
          }
          newButton={
            this.props.onNew ? (
              <Fab color="primary" onClick={this.props.onNew}>
                +
              </Fab>
            ) : null
          }
          showContentOverlay={false}
          {...this.props.SearchPageLayoutProps}
        />
      </React.Fragment>
    );
  }
}

export const searchFactory = () => withRouter(Search);
